import React, { useState, useMemo } from 'react';
import { 
  Calendar, Search, Filter, Heart, Users, MessageSquare, 
  Bookmark, Share2, ChevronDown, ChevronRight, Pin, 
  Star, AlertCircle, Info, Megaphone, Clock
} from 'lucide-react';
import { MarkdownRenderer } from '../components/ui/MarkdownRenderer';
import { Badge } from '../components/ui/Badge';
import { useAnnouncements, useAnnouncementInteractions } from '../hooks/useAnnouncements';
import { AnnouncementType, AnnouncementPriority } from '../types/announcements';

/**
 * AnnouncementsPage - Modern design member announcements page
 * 
 * Features:
 * - Filter by type, priority, and search
 * - Interactive announcement cards with reactions
 * - Modern design system with clean colors
 * - Responsive layout with mobile support
 */
const AnnouncementsPage: React.FC = () => {
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<AnnouncementType | 'all'>('all');
  const [selectedPriority, setSelectedPriority] = useState<AnnouncementPriority | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Fetch announcements with dynamic filtering
  const announcementParams: any = {
    orderBy: 'publishedAt' as const,
    orderDirection: 'desc' as const
  };
  
  if (selectedType !== 'all') announcementParams.type = selectedType;
  if (selectedPriority !== 'all') announcementParams.priority = selectedPriority;
  if (searchTerm.trim()) announcementParams.search = searchTerm.trim();
  
  const { data: announcementsData, isLoading, error } = useAnnouncements(announcementParams);

  const { addInteraction } = useAnnouncementInteractions();

  // Filter and sort announcements
  const filteredAnnouncements = useMemo(() => {
    if (!announcementsData?.announcements) return [];
    
    let filtered = announcementsData.announcements;

    // Client-side search if needed (backend should handle this)
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(announcement => 
        announcement.title.toLowerCase().includes(search) ||
        announcement.content.toLowerCase().includes(search)
      );
    }

    // Sort pinned first, then by priority, then by date
    return filtered.sort((a, b) => {
      // Pinned first
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;

      // Then by priority
      const priorityOrder = { critical: 4, important: 3, memorial: 2, general: 1 };
      const aPriority = priorityOrder[a.priority] || 1;
      const bPriority = priorityOrder[b.priority] || 1;
      if (aPriority !== bPriority) return bPriority - aPriority;

      // Finally by published date
      const aDate = new Date(a.publishedAt || a.createdAt);
      const bDate = new Date(b.publishedAt || b.createdAt);
      return bDate.getTime() - aDate.getTime();
    });
  }, [announcementsData?.announcements, searchTerm]);

  // Handle interactions
  const handleInteraction = async (announcementId: string, interactionType: string) => {
    try {
      await addInteraction.mutateAsync({
        id: announcementId,
        interaction: { interactionType: interactionType as any }
      });
    } catch (error) {
      console.error('Failed to add interaction:', error);
    }
  };

  // Get announcement type icon and color
  const getTypeDisplay = (type: AnnouncementType) => {
    const typeConfig = {
      meeting: { icon: Users, color: 'blue', label: 'Meeting' },
      birthday: { icon: Heart, color: 'pink', label: 'Birthday' },
      event: { icon: Calendar, color: 'green', label: 'Event' },
      memorial: { icon: Heart, color: 'purple', label: 'Memorial' },
      fundraiser: { icon: Heart, color: 'emerald', label: 'Fundraiser' },
      general: { icon: Info, color: 'gray', label: 'General' }
    };
    return typeConfig[type] || typeConfig.general;
  };

  // Get priority display
  const getPriorityDisplay = (priority: AnnouncementPriority) => {
    const priorityConfig = {
      critical: { color: 'red', label: 'Critical', icon: AlertCircle },
      important: { color: 'orange', label: 'Important', icon: Star },
      memorial: { color: 'purple', label: 'Memorial', icon: Heart },
      general: { color: 'gray', label: 'General', icon: Info }
    };
    return priorityConfig[priority] || priorityConfig.general;
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (error) {
    return (
      <div className="min-h-screen pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-red-100 border border-red-300 rounded-lg p-8">
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-red-900 mb-2">Unable to load announcements</h2>
            <p className="text-red-800">Please try again later or contact support if the problem persists.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-text-primary">
      {/* Header Section */}
      <div className="pt-32 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-3 bg-surface-1 px-6 py-3 rounded-lg border border-border mb-6">
              <Megaphone className="w-6 h-6 text-brand" />
              <span className="text-text-secondary text-sm font-medium">Family Updates</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-display font-bold mb-4 text-text-primary">
              Family Announcements
            </h1>
            
            <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
              Stay connected with the latest family news, upcoming events, and important updates from the Redd Family Association.
            </p>
          </div>

          {/* Search and Filter Section */}
          <div className="bg-surface-1 rounded-lg border border-border p-6 mb-8">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between mb-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-tertiary" />
                <input
                  type="text"
                  placeholder="Search announcements..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-border rounded-lg bg-white focus:border-brand focus:ring-0 focus:outline-none text-text-primary placeholder-text-tertiary"
                />
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 px-6 py-3 bg-brand hover:bg-brand-dark text-white rounded-lg transition-all font-semibold shadow-sm"
              >
                <Filter className="w-5 h-5" />
                <span>Filters</span>
                {showFilters ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            </div>

            {/* Expandable Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Type</label>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value as AnnouncementType | 'all')}
                    className="w-full p-3 border border-border rounded-lg bg-white focus:border-brand focus:ring-0 focus:outline-none text-text-primary"
                  >
                    <option value="all">All Types</option>
                    <option value="meeting">Meetings</option>
                    <option value="birthday">Birthdays</option>
                    <option value="event">Events</option>
                    <option value="memorial">Memorials</option>
                    <option value="fundraiser">Fundraisers</option>
                    <option value="general">General</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Priority</label>
                  <select
                    value={selectedPriority}
                    onChange={(e) => setSelectedPriority(e.target.value as AnnouncementPriority | 'all')}
                    className="w-full p-3 border border-border rounded-lg bg-white focus:border-brand focus:ring-0 focus:outline-none text-text-primary"
                  >
                    <option value="all">All Priorities</option>
                    <option value="critical">Critical</option>
                    <option value="important">Important</option>
                    <option value="memorial">Memorial</option>
                    <option value="general">General</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Announcements List */}
      <div className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
              <p className="text-text-secondary text-lg">Loading announcements...</p>
            </div>
          ) : filteredAnnouncements.length === 0 ? (
            <div className="text-center py-12">
              <div className="bg-surface-1 rounded-lg p-12 border border-border max-w-2xl mx-auto">
                <MessageSquare className="w-16 h-16 text-text-tertiary mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-text-primary mb-2">No announcements found</h3>
                <p className="text-text-secondary">
                  {searchTerm || selectedType !== 'all' || selectedPriority !== 'all'
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Check back soon for family updates and announcements.'
                  }
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredAnnouncements.map((announcement) => {
                const typeDisplay = getTypeDisplay(announcement.type);
                const priorityDisplay = getPriorityDisplay(announcement.priority);
                const TypeIcon = typeDisplay.icon;
                const PriorityIcon = priorityDisplay.icon;

                return (
                  <div
                    key={announcement.id}
                    className={`bg-surface-1 rounded-lg border transition-all hover:shadow-sm ${
                      announcement.isPinned ? 'border-brand' : 'border-border'
                    } ${announcement.priority === 'critical' ? 'ring-2 ring-red-300' : ''}`}
                  >
                    {/* Header */}
                    <div className="p-6 border-b border-border">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          {announcement.isPinned && (
                            <Pin className="w-5 h-5 text-brand fill-current" />
                          )}
                          <div className={`w-10 h-10 bg-brand rounded-lg flex items-center justify-center shadow-sm`}>
                            <TypeIcon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h2 className="text-2xl font-bold text-text-primary leading-tight">
                              {announcement.title}
                            </h2>
                            <div className="flex items-center space-x-4 mt-1">
                              <Badge variant="outline">
                                {typeDisplay.label}
                              </Badge>
                              {announcement.priority !== 'general' && (
                                <Badge variant="outline" className="gap-1">
                                  <PriorityIcon className="w-3 h-3" />
                                  {priorityDisplay.label}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right text-sm text-text-secondary">
                          <div className="flex items-center space-x-1 mb-1">
                            <Clock className="w-4 h-4" />
                            <span>
                              {announcement.startDate ? (
                                announcement.endDate && announcement.startDate !== announcement.endDate
                                  ? `${formatDate(announcement.startDate)} - ${formatDate(announcement.endDate)}`
                                  : formatDate(announcement.startDate)
                              ) : (
                                formatDate(announcement.publishedAt || announcement.createdAt)
                              )}
                            </span>
                          </div>
                          {announcement.isFeatured && (
                            <Badge className="bg-brand text-white gap-1">
                              <Star className="w-3 h-3 fill-current" />
                              Featured
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      {announcement.excerpt && (
                        <div className="text-lg text-text-secondary mb-4 font-medium italic">
                          <MarkdownRenderer 
                            content={announcement.excerpt}
                            proseColor="slate"
                            className="prose-lg"
                          />
                        </div>
                      )}
                      <MarkdownRenderer 
                        content={announcement.content}
                        proseColor="slate"
                        className="max-w-none"
                      />

                      {/* Dates */}
                      {(announcement.startDate || announcement.endDate) && (
                        <div className="mt-6 p-4 bg-surface-2 rounded-lg border border-border">
                          <div className="flex items-center space-x-2 mb-2">
                            <Calendar className="w-5 h-5 text-brand" />
                            <span className="font-semibold text-text-primary">Dates</span>
                          </div>
                          {announcement.startDate && (
                            <p className="text-text-secondary">
                              <span className="font-medium">Start:</span> {formatDate(announcement.startDate)}
                            </p>
                          )}
                          {announcement.endDate && (
                            <p className="text-text-secondary">
                              <span className="font-medium">End:</span> {formatDate(announcement.endDate)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Interactions Footer */}
                    <div className="px-6 py-4 border-t border-border bg-surface-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-6 text-sm text-text-secondary">
                          <span className="flex items-center space-x-1">
                            <Users className="w-4 h-4" />
                            <span>{announcement.interactions.views} views</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Heart className="w-4 h-4" />
                            <span>{announcement.interactions.likes + announcement.interactions.loves} reactions</span>
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleInteraction(announcement.id, 'like')}
                            className="flex items-center space-x-1 px-3 py-2 bg-white hover:bg-surface-1 border border-border rounded-lg transition-all text-text-secondary font-medium"
                            disabled={addInteraction.isPending}
                          >
                            <Heart className="w-4 h-4" />
                            <span>Like</span>
                          </button>
                          
                          <button
                            onClick={() => handleInteraction(announcement.id, 'bookmark')}
                            className="flex items-center space-x-1 px-3 py-2 bg-white hover:bg-surface-1 border border-border rounded-lg transition-all text-text-secondary font-medium"
                            disabled={addInteraction.isPending}
                          >
                            <Bookmark className="w-4 h-4" />
                            <span>Save</span>
                          </button>
                          
                          <button
                            onClick={() => handleInteraction(announcement.id, 'share')}
                            className="flex items-center space-x-1 px-3 py-2 bg-white hover:bg-surface-1 border border-border rounded-lg transition-all text-text-secondary font-medium"
                            disabled={addInteraction.isPending}
                          >
                            <Share2 className="w-4 h-4" />
                            <span>Share</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { AnnouncementsPage };
